import { isEmpty } from 'lodash';

import { CarrierLogo } from '@/components/carrier-logo';
import { CountryFlag } from '@/components/country-flag';
import { Heading } from '@/components/heading';
import { VisuallyHidden } from '@/components/visually-hidden';
import { AIRPORT, ID_CANONICAL, MODE_AIR, MODE_WATER, SEAPORT } from '@/lib/constants';
import { CarrierType } from '@/types';
import { IdentifierApiInterface, NodeType, SegmentModeType } from '@/types/api-types';
import { getPreferredIdentifier } from '@/utils/helpers';
import { tv } from '@/utils/styles';

interface Props {
  identifiers: IdentifierApiInterface[];
  type: NodeType | CarrierType | string;
  countryCode?: string | null;
  screenReaderMsg?: string;
}

const convertTypeToMode = (type: NodeType | CarrierType | string): SegmentModeType => {
  switch (type) {
    case 'Air':
    case AIRPORT:
      return MODE_AIR;
    case 'Ocean':
    case SEAPORT:
      return MODE_WATER;
    default:
      return type as SegmentModeType;
  }
};

const IdentifierPanel = ({
  identifiers,
  type,
  countryCode,
  screenReaderMsg = 'This carrier is also represented by the following industry standard Carrier Codes.',
}: Props) => {
  const filteredIdentifiers = identifiers.filter((id) => id.type !== ID_CANONICAL);
  const carrierCode = getPreferredIdentifier(filteredIdentifiers)?.value;
  const carrierMode = convertTypeToMode(type);

  // return if identifiers missing or empty
  if (isEmpty(filteredIdentifiers)) return null;

  const sortedIdentifiers = filteredIdentifiers.sort((a, b) => a.type.localeCompare(b.type));

  const { base, logo, ul, li } = styles();

  return (
    <div className={base()} data-cy="identifier-panel">
      <div className={logo()}>
        {!countryCode && carrierCode && <CarrierLogo carrierCode={carrierCode} mode={carrierMode} />}
        {countryCode && <CountryFlag countryCode={countryCode} />}
      </div>
      <VisuallyHidden>
        <Heading>{screenReaderMsg}</Heading>
      </VisuallyHidden>
      <ul className={ul()}>
        {sortedIdentifiers.map((identity) => (
          <li className={li()} key={identity.type}>
            {identity.type.toUpperCase()}: <strong>{identity.value}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'inline-flex items-center rounded-[1.25rem] bg-white leading-none text-text-primary [&_strong]:font-semibold',
    logo: 'py-2 pl-4 pr-0',
    ul: 'flex items-center',
    li: 'border-r border-r-grey-200 px-4 py-2 last:border-r-0',
  },
});

export { IdentifierPanel };
